import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  FileText,
  Clock,
  UserCircle,
  Settings,
  CircleDollarSign,
  IdCard,
} from "lucide-react";

const VARIANTS = {
  profile: {
    intro:
      "Your details won't be shared now, but they'll be stored to share in future rental applications.",
    rows: [
      {
        Icon: Clock,
        text: "We store details for 18 months (6 months if your current address is in SA), and documents for 60 days.",
      },
      {
        Icon: Settings,
        text: "You can update these details any time, but changes won't apply to submitted rental applications.",
      },
    ],
    actionLabel: "Learn more in the Help Centre",
  },
  personalDetails: {
    intro:
      "Your personal details will be stored to share in future rental applications.",
    rows: [
      {
        Icon: FileText,
        text: "Provide your details once to save time when completing rental applications.",
      },
      {
        Icon: Clock,
        text: "Personal details are stored for 18 months (6 months in SA).",
      },
      {
        Icon: UserCircle,
        text: "We share what you provide in this form, along with your member email.",
      },
      {
        Icon: Settings,
        text: "You can update these details any time, but changes won't apply to submitted rental applications.",
      },
    ],
    actionLabel: "Learn more in the Privacy Centre",
  },
  addressHistory: {
    intro:
      "Your address history and references will be stored to share in future rental applications.",
    rows: [
      {
        Icon: FileText,
        text: "Provide your address history once to save time when completing rental applications.",
      },
      {
        Icon: Clock,
        text: "Address history and reference details are stored for 18 months (6 months in SA).",
      },
      {
        Icon: Settings,
        text: "You can update these details any time, but changes won't apply to submitted rental applications.",
      },
    ],
    actionLabel: "Learn more in the Privacy Centre",
  },
  employment: {
    intro:
      "Your employment and reference details will be stored to share in future rental applications.",
    rows: [
      {
        Icon: FileText,
        text: "Provide your employment information once and save time when completing rental applications.",
      },
      {
        Icon: Clock,
        text: "Employment and reference details are stored for 18 months (6 months in SA).",
      },
      {
        Icon: Settings,
        text: "You can update these details any time, but changes won't apply to submitted rental applications.",
      },
    ],
    actionLabel: "Learn more in the Privacy Centre",
  },
  finances: {
    intro:
      "Income details provided here will be stored to share in future rental applications.",
    rows: [
      {
        Icon: CircleDollarSign,
        text: "Add your income sources once to save time when completing rental applications.",
      },
      {
        Icon: Clock,
        text: "Income sources are stored for 18 months (6 months in SA), and documents are stored for 60 days.",
      },
      {
        Icon: Settings,
        text: "You can update these details any time, but changes won't apply to submitted rental applications.",
      },
    ],
    actionLabel: "Learn more in the Help Centre",
  },
  identityDocuments: {
    intro:
      "Your ID details and images will be stored to share in future rental applications.",
    rows: [
      {
        Icon: IdCard,
        text: "Provide your ID details once to save time when completing rental applications.",
      },
      {
        Icon: Clock,
        text: "Details and documents are stored for 60 days.",
      },
      {
        Icon: Settings,
        text: "You can update these details any time, but changes won't apply to submitted rental applications.",
      },
    ],
    actionLabel: "Learn more in the Help Centre",
  },
};

const RentalDataUseModal = ({ open, onClose, variant = "profile" }) => {
  const navigate = useNavigate();
  const config = VARIANTS[variant] ?? VARIANTS.profile;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="data-use-modal-title"
        className="w-full max-w-lg rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#E5E7EB] px-6 pb-4 pt-6">
          <h2
            id="data-use-modal-title"
            className="text-lg font-semibold text-[#333333]"
          >
            Collecting your data
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-[#6B7280] transition-colors hover:bg-[#F3F4F6] hover:text-[#333333]"
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm leading-relaxed text-[#555555]">
            {config.intro}
          </p>

          <ul className="mt-6 list-none space-y-5 p-0">
            {config.rows.map(({ Icon, text }) => (
              <li key={text} className="flex gap-3">
                <Icon
                  className="mt-0.5 h-5 w-5 shrink-0 text-[#555555]"
                  strokeWidth={1.5}
                />
                <p className="text-sm leading-relaxed text-[#555555]">{text}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-center border-t border-[#E5E7EB] px-6 py-5">
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate("/contactus");
            }}
            className="w-full max-w-sm rounded-lg border border-[#D1D5DB] bg-white px-4 py-2.5 text-sm font-medium text-[#333333] transition-colors hover:bg-[#F9FAFB]"
          >
            {config.actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RentalDataUseModal;
